import React, { useEffect, useState } from 'react';

const DISMISS_KEY = 'chargeos-install-dismissed-at';
const SNOOZE_DAYS = 14;

const isStandalone = () => window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
const isIosSafari = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && /safari/i.test(navigator.userAgent) && !/crios|fxios|edgios/i.test(navigator.userAgent);
const snoozed = () => {
  const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return at && Date.now() - at < SNOOZE_DAYS * 86400000;
};

/**
 * Invites phone and tablet users to install ChargeOS as an app. Chrome / Android offer a real install
 * prompt; iOS Safari has none, so it gets the "Share, then Add to Home Screen" hint instead.
 * Never shown when already installed, on desktop, or within two weeks of being dismissed.
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [mode, setMode] = useState(null); // 'prompt' | 'ios'

  useEffect(() => {
    if (isStandalone() || snoozed() || window.innerWidth >= 992) return undefined;
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setMode('prompt');
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    const onInstalled = () => setMode(null);
    window.addEventListener('appinstalled', onInstalled);
    // Give the page a moment before nudging on iOS.
    const t = isIosSafari() ? setTimeout(() => setMode('ios'), 2500) : null;
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
      if (t) clearTimeout(t);
    };
  }, []);

  if (!mode) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setMode(null);
  };
  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice.catch(() => null);
    dismiss();
  };

  return (
    <div className="chargeos-install" role="dialog" aria-label="Install ChargeOS" id="install-prompt">
      <div className="chargeos-install-icon"><i className="fas fa-bolt" aria-hidden="true" /></div>
      <div className="chargeos-install-text">
        <strong>Install ChargeOS</strong>
        {mode === 'ios'
          ? <span>Tap <i className="fas fa-arrow-up-from-bracket" aria-label="Share" /> then <em>Add to Home Screen</em> for a full-screen app.</span>
          : <span>Add it to your home screen for a full-screen app.</span>}
      </div>
      {mode === 'prompt' && <button type="button" className="btn btn-sm btn-primary" id="btn-install-app" onClick={install}>Install</button>}
      <button type="button" className="btn-close" aria-label="Not now" onClick={dismiss} />
    </div>
  );
}
